import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import DateTimePicker from '../components/DateTimePicker';
import '../pages/CreateEvent.css';
import {
    ArrowLeft, Calendar, MapPin, Tag, DollarSign, Users,
    Link as LinkIcon, Image as ImageIcon, Upload, X, Globe,
    AlignLeft, Link2, Ticket
} from 'lucide-react';

const CATEGORIES = [
    'Music', 'Dance', 'Festival', 'Food', 'Workshop',
    'Film', 'Exhibition', 'Nightlife', 'Spiritual', 'Literature',
    'Percussion', 'Cultural Association', 'Other'
];

export default function CreateEventPage() {
    const { user, loading: authLoading, isAdmin } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const fileInputRef = useRef(null);

    const [form, setForm] = useState({
        title: '', description: '', date: '', end_date: '', location: '',
        category: 'Music', price: '', capacity: '', organizer: '',
        image_url: '', external_booking_url: '', tags: ''
    });

    const [isDirty, setIsDirty] = useState(false);

    const [previewImage, setPreviewImage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [loadingEdit, setLoadingEdit] = useState(isEditMode);

    // Helper to get full image URL for preview
    const getPreviewUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        return `${baseUrl}${url}`;
    };

    // ── Unsaved changes guard ──────────────────────────────────────

    useEffect(() => {
        const handler = (e) => {
            if (isDirty && !submitting) { e.preventDefault(); e.returnValue = ''; }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty, submitting]);
    // ──────────────────────────────────────────────────────────────

    useEffect(() => {
        if (isEditMode && isAdmin) loadExistingEvent();
    }, [isEditMode, isAdmin]);

    const loadExistingEvent = async () => {
        try {
            const event = await api.getEvent(id);
            if (!event) { navigate('/admin'); return; }
            setPreviewImage(event.image_url);
            setForm({
                title: event.title || '',
                description: event.description || '',
                date: event.date ? new Date(event.date).toISOString().slice(0, 16) : '',
                end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
                location: event.location || '',
                category: event.category || 'Music',
                price: event.price || '',
                capacity: event.capacity || '',
                organizer: event.organizer || '',
                image_url: event.image_url || '',
                external_booking_url: event.external_booking_url || '',
                tags: Array.isArray(event.tags) ? event.tags.join(', ') : (event.tags || '')
            });
        } catch (err) { console.error(err); }
        finally { setLoadingEdit(false); }
    };

    const handleChange = (e) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setError('');
        setIsDirty(true);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { setError('Image must be less than 5MB'); return; }
        try {
            setUploading(true);
            const data = await api.uploadImage(file);
            setForm(prev => ({ ...prev, image_url: data.url }));
            setPreviewImage(data.url);
        } catch (err) { setError('Failed to upload image.'); }
        finally { setUploading(false); }
    };

    const handleSubmit = async () => {
        setError('');
        if (!form.title.trim()) { setError('Event title is required'); return; }
        if (!form.date) { setError('Start date is required'); return; }
        if (!form.location.trim()) { setError('Location is required'); return; }
        const payload = {
            ...form,
            date: new Date(form.date).toISOString(),
            end_date: form.end_date ? new Date(form.end_date).toISOString() : undefined,
            price: parseFloat(form.price) || 0,
            capacity: form.capacity ? parseInt(form.capacity) : undefined,
            tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags
        };
        try {
            setSubmitting(true);
            if (isEditMode) { await api.updateEvent(id, payload); }
            else { await api.createEvent(payload); }
            navigate('/admin');
        } catch (err) { setError(err.message || 'Failed to save event.'); setSubmitting(false); }
    };

    if (authLoading || loadingEdit) return <div className="cef-loading">Loading...</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!isAdmin) return <Navigate to="/dashboard" replace />;

    return (
        <div className="cef-wrapper">
            <div className="cef-header">
                <button className="cef-back-btn" onClick={() => navigate('/admin')}>
                    <ArrowLeft size={16} />
                    <span>Back to Admin</span>
                </button>
                <div>
                    <h1 className="cef-page-title">{isEditMode ? 'Edit Event' : 'Create New Event'}</h1>
                    <p className="cef-page-subtitle">{isEditMode ? 'Update this event on the platform.' : 'Publish a new event directly to the platform.'}</p>
                </div>
            </div>

            {error && <div className="cef-error">{error}</div>}

            <div className="cef-grid">
                {/* ── LEFT COLUMN ── */}
                <div className="cef-left">
                    {/* Image Upload */}
                    <div className="cef-card">
                        <label className="cef-section-label"><ImageIcon size={14} /> Event Poster</label>
                        <div
                            className="cef-image-box"
                            onClick={() => fileInputRef.current?.click()}
                            style={previewImage ? { padding: 0, overflow: 'hidden' } : {}}
                        >
                            {previewImage ? (
                                <>
                                    <img src={getPreviewUrl(previewImage)} alt="preview" className="cef-image-preview" />
                                    <button
                                        className="cef-remove-img"
                                        type="button"
                                        onClick={e => { e.stopPropagation(); setPreviewImage(null); setForm(p => ({ ...p, image_url: '' })); fileInputRef.current && (fileInputRef.current.value = ''); }}
                                    >
                                        <X size={14} />
                                    </button>
                                </>
                            ) : (
                                uploading ? (
                                    <div className="cef-spinner" />
                                ) : (
                                    <div className="cef-image-placeholder">
                                        <img src="/atam-logo.svg" alt="ATAM" className="cef-logo-placeholder" />
                                        <span className="cef-upload-label"><Upload size={16} /> Upload Poster</span>
                                        <span className="cef-upload-hint">JPG, PNG, WEBP — Max 5MB</span>
                                    </div>
                                )
                            )}
                        </div>
                        <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleImageUpload} />
                        <div className="cef-field" style={{ marginTop: 12 }}>
                            <label className="cef-field-label"><Link2 size={12} /> Or paste image URL</label>
                            <input
                                className="cef-input"
                                name="image_url"
                                value={form.image_url}
                                onChange={e => { handleChange(e); setPreviewImage(e.target.value); }}
                                placeholder="https://..."
                            />
                        </div>
                    </div>

                    {/* Category */}
                    <div className="cef-card">
                        <label className="cef-section-label"><Tag size={14} /> Category</label>
                        <select className="cef-select" name="category" value={form.category} onChange={handleChange}>
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    {/* Ticketing */}
                    <div className="cef-card">
                        <label className="cef-section-label"><Ticket size={14} /> Ticket & Capacity</label>
                        <div className="cef-row">
                            <div className="cef-field">
                                <label className="cef-field-label"><DollarSign size={12} /> Price (SGD)</label>
                                <input className="cef-input" type="number" name="price" value={form.price} onChange={handleChange} min="0" step="0.01" placeholder="0 = Free" />
                            </div>
                            <div className="cef-field">
                                <label className="cef-field-label"><Users size={12} /> Capacity</label>
                                <input className="cef-input" type="number" name="capacity" value={form.capacity} onChange={handleChange} min="1" placeholder="Unlimited" />
                            </div>
                        </div>
                        <div className="cef-field" style={{ marginTop: 12 }}>
                            <label className="cef-field-label"><Globe size={12} /> External Booking URL</label>
                            <input className="cef-input" name="external_booking_url" value={form.external_booking_url} onChange={handleChange} placeholder="https://bookmyshow.com/..." />
                        </div>
                    </div>
                </div>

                {/* ── RIGHT COLUMN ── */}
                <div className="cef-right">
                    {/* Core Details */}
                    <div className="cef-card">
                        <label className="cef-section-label">Event Details</label>
                        <div className="cef-field">
                            <label className="cef-field-label">Event Title *</label>
                            <input className="cef-input cef-title-input" name="title" value={form.title} onChange={handleChange} placeholder="e.g. Tamil New Year Celebration" />
                        </div>
                        <div className="cef-field">
                            <label className="cef-field-label"><AlignLeft size={12} /> Description *</label>
                            <textarea className="cef-textarea" name="description" value={form.description} onChange={handleChange} rows={5} placeholder="Describe the event — performances, artists, what to expect..." />
                        </div>
                        <div className="cef-field">
                            <label className="cef-field-label">Organizer / Group</label>
                            <input className="cef-input" name="organizer" value={form.organizer} onChange={handleChange} placeholder="e.g. ATAM Dance Academy" />
                        </div>
                    </div>

                    {/* Date & Time */}
                    <div className="cef-card">
                        <label className="cef-section-label"><Calendar size={14} /> Date &amp; Time</label>
                        <div className="cef-row">
                            <div className="cef-field">
                                <label className="cef-field-label">Start *</label>
                                <DateTimePicker
                                    label="Start"
                                    placeholder="Pick start date & time"
                                    value={form.date}
                                    onChange={(v) => { setForm(p => ({ ...p, date: v })); setError(''); }}
                                />
                            </div>
                            <div className="cef-field">
                                <label className="cef-field-label">End</label>
                                <DateTimePicker
                                    label="End"
                                    placeholder="Pick end date & time"
                                    value={form.end_date}
                                    min={form.date}
                                    onChange={(v) => { setForm(p => ({ ...p, end_date: v })); setError(''); }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Location & Tags */}
                    <div className="cef-card">
                        <label className="cef-section-label"><MapPin size={14} /> Location & Tags</label>
                        <div className="cef-field">
                            <label className="cef-field-label">Venue / Location *</label>
                            <input className="cef-input" name="location" value={form.location} onChange={handleChange} placeholder="e.g. Esplanade, Singapore" />
                        </div>
                        <div className="cef-field">
                            <label className="cef-field-label"><Tag size={12} /> Tags (comma-separated)</label>
                            <input className="cef-input" name="tags" value={form.tags} onChange={handleChange} placeholder="Classical, Family-friendly, Live-music" />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="cef-actions">
                        <button className="cef-btn-secondary" type="button" onClick={() => navigate('/admin')}>Cancel</button>
                        <button className="cef-btn-primary" type="button" onClick={handleSubmit} disabled={submitting}>
                            {submitting ? 'Saving…' : (isEditMode ? '✔ Update Event' : '🚀 Publish Event')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
